import { P as PubSub } from './pubsub-CoPYzvVm.js';
import './notyf-DIw0RvBj.js';

/**
 * Gates the add to cart flow (main button + sticky bar) behind a size choice.
 *
 * The colour stays pre-selected; the size does not. Until a size radio is picked
 * the buttons read "select size" (rendered server-side by snippets/size-gate.liquid)
 * and tapping one scrolls to the size options instead of adding to the cart.
 *
 * Server-side the gate is on while no variant is pinned in the URL. A client-side
 * variant change (e.g. switching colour) re-fetches the section with a variant
 * pinned, so the markup comes back with the gate open — this element closes it
 * again until an actual size has been chosen.
 */
class SizeGate extends HTMLElement {
  #unsubscribers = [];
  #productId = this.dataset.productId;
  #selectSizeLabel = this.dataset.selectSizeLabel || '';
  #addToCartLabel = this.dataset.addToCartLabel || '';
  #sizeChosen = new URLSearchParams(window.location.search).has('variant');

  connectedCallback() {
    // Only active when the size options are actually gated (see size-gate.liquid).
    if (!document.querySelector('[data-size-fieldset]')) return;

    document.addEventListener('change', this.#onChange, true);
    document.addEventListener('click', this.#onClick, true);

    this.#unsubscribers.push(
      PubSub.subscribe('variant:change', ({ id }) => {
        if (id !== this.#productId || this.#sizeChosen) return;
        requestAnimationFrame(() => this.#closeGate());
      })
    );

    if (!this.#sizeChosen) this.#closeGate();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#onChange, true);
    document.removeEventListener('click', this.#onClick, true);
    this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
  }

  /**
   * A size radio change is the shopper choosing a size — open the gate.
   * @param {Event} event
   */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (!input.closest('[data-size-fieldset]')) return;

    this.#sizeChosen = true;
    this.#openGate();
  };

  /**
   * Intercept a tap on a gated add to cart button: scroll to the size options
   * instead of submitting the form.
   * @param {Event} event
   */
  #onClick = (event) => {
    if (this.#sizeChosen) return;
    const button = event.target.closest?.('[data-size-gate]');
    if (!button) return;

    const formId = button.getAttribute('form') || '';
    if (formId.indexOf(`product-form-${this.#productId}`) !== 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#scrollToSize();
  };

  /**
   * @returns {HTMLButtonElement[]} The add to cart buttons for this product.
   */
  #buttons() {
    return [
      ...document.querySelectorAll(`button[form^="product-form-${this.#productId}"]`),
    ];
  }

  /**
   * Gate closed: no size chosen yet — show "select size", disable the savings line.
   */
  #closeGate() {
    this.#buttons().forEach((button) => {
      button.setAttribute('data-size-gate', '');
      const label = button.querySelector('.button__label');
      if (label) label.textContent = this.#selectSizeLabel;
      const savings = button.querySelector('.button__savings');
      if (savings) savings.hidden = true;
    });

    document
      .querySelectorAll('[data-size-fieldset] input[type="radio"]:checked')
      .forEach((radio) => {
        radio.checked = false;
      });

    const selectedValue = document.querySelector('[data-size-fieldset] [data-selected-value]');
    if (selectedValue) selectedValue.textContent = '';
  }

  /**
   * Gate open: a size was picked. Restore the add to cart label right away; the
   * pending section re-fetch fills in the rest (price, savings, stock).
   */
  #openGate() {
    this.#buttons().forEach((button) => {
      if (!button.hasAttribute('data-size-gate')) return;
      button.removeAttribute('data-size-gate');
      const label = button.querySelector('.button__label');
      if (label) label.textContent = this.#addToCartLabel;
    });
  }

  #scrollToSize() {
    const fieldset = document.querySelector('[data-size-fieldset]');
    if (!fieldset) return;

    const headerHeight =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        10
      ) || 0;
    const top = fieldset.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top, behavior: prefersReducedMotion ? 'auto' : 'smooth' });

    const firstRadio = fieldset.querySelector('input[type="radio"]');
    if (firstRadio) firstRadio.focus({ preventScroll: true });
  }
}

customElements.define('size-gate', SizeGate);
